/**
 * TrendDrillDownModal Component
 *
 * Drill-down modal for a trend chart month.
 * Shows P&L detail breakdown, month context events, and AI metric insight.
 * Follows CashFlowDetailModal pattern for accessibility and UX.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import type {
  MonthlyTrendPoint,
  MonthEvent,
  TrendInsight,
  CreateMonthEventInput,
  EventCategory,
  EventImpact,
} from '@/types/scalingUp';
import { CommentButton } from './CommentButton';
import './TrendDrillDownModal.css';

interface TrendDrillDownModalProps {
  month: MonthlyTrendPoint;
  events: MonthEvent[];
  insight: TrendInsight | null;
  metricLabel: string;
  onClose: () => void;
  onAddEvent: (input: CreateMonthEventInput) => Promise<MonthEvent | null>;
  onDeleteEvent: (id: string) => Promise<boolean>;
}

const CATEGORY_LABELS: Record<EventCategory, string> = {
  staff: 'Staff',
  operational: 'Operational',
  financial: 'Financial',
  external: 'External',
};

const IMPACT_COLORS: Record<EventImpact, string> = {
  positive: 'var(--color-green)',
  negative: '#e05252',
  neutral: 'var(--color-navy)',
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value);
}

const DIRECTION_LABELS: Record<TrendInsight['direction'], string> = {
  improving: '↑ Improving',
  declining: '↓ Declining',
  stable: '→ Stable',
  volatile: '~ Volatile',
};

export function TrendDrillDownModal({
  month,
  events,
  insight,
  metricLabel,
  onClose,
  onAddEvent,
  onDeleteEvent,
}: TrendDrillDownModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [addingEvent, setAddingEvent] = useState(false);
  const [formCategory, setFormCategory] = useState<EventCategory>('operational');
  const [formDescription, setFormDescription] = useState('');
  const [formImpact, setFormImpact] = useState<EventImpact>('neutral');
  const [submitting, setSubmitting] = useState(false);
  const [eventError, setEventError] = useState<string | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    modalRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmitEvent = useCallback(async () => {
    if (!formDescription.trim()) return;
    setSubmitting(true);
    setEventError(null);
    const input: CreateMonthEventInput = {
      month: month.month,
      category: formCategory,
      description: formDescription.trim(),
      impact: formImpact,
    };
    const result = await onAddEvent(input);
    if (result) {
      setFormDescription('');
      setFormCategory('operational');
      setFormImpact('neutral');
      setAddingEvent(false);
    } else {
      setEventError('Failed to save event. Please try again.');
    }
    setSubmitting(false);
  }, [formDescription, formCategory, formImpact, month.month, onAddEvent]);

  const detail = month.detail;

  return (
    <div
      className="tdd-overlay"
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        className="tdd-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${month.label} ${month.month} — ${metricLabel} detail`}
        ref={modalRef}
        tabIndex={-1}
      >
        <div className="tdd-header">
          <div>
            <h2 className="tdd-title">{month.label} {month.month}</h2>
            <p className="tdd-subtitle">{metricLabel} drill-down</p>
          </div>
          <div className="tdd-header-actions">
            <CommentButton targetType="trend-chart" targetId={`month-${month.month}`} />
            <button
              className="tdd-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        </div>

        <div className="tdd-body">
          {/* P&L Breakdown */}
          {detail ? (
            <section className="tdd-section">
              <h3 className="tdd-section-title">P&amp;L Breakdown</h3>
              <table className="tdd-table">
                <tbody>
                  <tr className="tdd-row-header">
                    <td>Revenue</td>
                    <td>{formatCurrency(month.revenue)}</td>
                  </tr>
                  {detail.cogsItems.map((item, i) => (
                    <tr key={`cogs-${i}-${item.name}`} className="tdd-row-sub">
                      <td>↳ {item.name}</td>
                      <td>{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  <tr className="tdd-row-total">
                    <td>Gross Margin</td>
                    <td>{formatCurrency(month.revenue - detail.cogs)} ({month.grossMarginPercent.toFixed(1)}%)</td>
                  </tr>
                  {detail.directLabourItems.map((item, i) => (
                    <tr key={`dl-${i}-${item.name}`} className="tdd-row-sub">
                      <td>↳ {item.name} (direct labour)</td>
                      <td>{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  {detail.otherExpenseItems.slice(0, 6).map((item, i) => (
                    <tr key={`exp-${i}-${item.name}`} className="tdd-row-sub">
                      <td>↳ {item.name}</td>
                      <td>{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                  <tr className="tdd-row-total">
                    <td>Net Income</td>
                    <td
                      style={{ color: detail.netIncome >= 0 ? 'var(--color-green)' : '#e05252' }}
                    >
                      {formatCurrency(detail.netIncome)}
                    </td>
                  </tr>
                  <tr>
                    <td>Cash Balance (month-end)</td>
                    <td>{formatCurrency(detail.cashBalance)}</td>
                  </tr>
                  <tr>
                    <td>Monthly Burn Rate</td>
                    <td>{formatCurrency(detail.burnRate)}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          ) : (
            <section className="tdd-section">
              <p className="tdd-empty">P&amp;L detail not available for this month.</p>
            </section>
          )}

          {/* AI Insight */}
          {insight && (
            <section className="tdd-section tdd-insight">
              <h3 className="tdd-section-title">
                AI Insight — {insight.metric}
                <span
                  className="tdd-direction"
                  data-direction={insight.direction}
                >
                  {DIRECTION_LABELS[insight.direction]}
                </span>
              </h3>
              <p className="tdd-insight-summary">{insight.summary}</p>
              {insight.keyDrivers.length > 0 && (
                <ul className="tdd-drivers">
                  {insight.keyDrivers.map((d, i) => (
                    <li key={`${i}-${d.slice(0, 12)}`}>{d}</li>
                  ))}
                </ul>
              )}
              {insight.dataQualityNote && (
                <p className="tdd-quality-note">⚠ {insight.dataQualityNote}</p>
              )}
            </section>
          )}

          {/* Month Events */}
          <section className="tdd-section">
            <div className="tdd-events-header">
              <h3 className="tdd-section-title">Context Events</h3>
              <button
                className="tdd-add-btn"
                onClick={() => setAddingEvent((v) => !v)}
              >
                {addingEvent ? 'Cancel' : '+ Add event'}
              </button>
            </div>

            {addingEvent && (
              <div className="tdd-event-form">
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value as EventCategory)}
                  className="tdd-select"
                >
                  {(Object.keys(CATEGORY_LABELS) as EventCategory[]).map((c) => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
                <select
                  value={formImpact}
                  onChange={(e) => setFormImpact(e.target.value as EventImpact)}
                  className="tdd-select"
                >
                  <option value="positive">Positive</option>
                  <option value="neutral">Neutral</option>
                  <option value="negative">Negative</option>
                </select>
                <textarea
                  className="tdd-textarea"
                  placeholder="Describe the event (max 500 chars)…"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  maxLength={500}
                  rows={3}
                />
                {eventError && (
                  <p className="tdd-event-error">{eventError}</p>
                )}
                <button
                  className="tdd-submit-btn"
                  onClick={handleSubmitEvent}
                  disabled={submitting || !formDescription.trim()}
                >
                  {submitting ? 'Saving…' : 'Save event'}
                </button>
              </div>
            )}

            {events.length === 0 && !addingEvent && (
              <p className="tdd-empty">No context events for this month.</p>
            )}

            <ul className="tdd-event-list">
              {events.map((event) => (
                <li key={event.id} className="tdd-event-item">
                  <div className="tdd-event-meta">
                    <span
                      className="tdd-event-impact"
                      style={{ color: IMPACT_COLORS[event.impact] }}
                    >
                      ●
                    </span>
                    <span className="tdd-event-category">
                      {CATEGORY_LABELS[event.category]}
                    </span>
                    {event.endMonth && (
                      <span className="tdd-event-range">
                        {event.month}–{event.endMonth}
                      </span>
                    )}
                  </div>
                  <p className="tdd-event-desc">{event.description}</p>
                  <button
                    className="tdd-event-delete"
                    onClick={() => onDeleteEvent(event.id)}
                    aria-label="Delete event"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
